import React from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import useCache from '../../app/useCache';
import { CachedEntity } from '../../app/cacheSlice';
import { setUserRoles } from '../../app/userSlice';
import { useCurrentUser } from '../../app/useCurrentUser';
import useStagedRoles from '../../app/useStagedRoles';
import RoleSelector from '../common/RoleSelector';

export const pendingChangesLabel = (added: number, removed: number) => {
    if (added === 0 && removed === 0) return 'No unsaved changes';
    if (removed === 0) return `+${added} unsaved change${added === 1 ? '' : 's'}`;
    if (added === 0) return `-${removed} unsaved change${removed === 1 ? '' : 's'}`;
    return `${added + removed} unsaved changes`;
};

export default function UserRolesPanel() {
    const dispatch = useAppDispatch();
    const userState = useAppSelector(store => store.user);
    const roles = useCache(CachedEntity.Role);
    const [isAdmin, ,] = useCurrentUser();

    const committedIds = (userState?.roles ?? []).map(r => r.id);
    const { draftIds, setDraftIds, added, removed, isDirty, reset } = useStagedRoles(committedIds);

    const disabled = !isAdmin || !userState?.data?.enabled;

    const handleSave = () => {
        dispatch(setUserRoles({ userId: userState.id, roleIds: draftIds }));
    };

    return (
        <React.Fragment>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                        <Typography variant="h5" color="primary">
                            User Roles
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Typography variant="body2" color="textSecondary">
                                {pendingChangesLabel(added.length, removed.length)}
                            </Typography>
                            <Button variant="outlined" onClick={reset} disabled={disabled || !isDirty}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={handleSave} disabled={disabled || !isDirty}>
                                Save
                            </Button>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <RoleSelector
                        roles={roles}
                        selectedIds={draftIds}
                        onChange={setDraftIds}
                        disabled={disabled}
                    />
                </Grid>
            </Grid>
        </React.Fragment>
    );
}
